/**
 * CalendarTab — vertical timeline of upcoming events.
 *
 * Source colour coding (left-bar):
 *   blue  = Google Calendar
 *   purple = Office 365
 *   brand = CRM meeting (linked to a deal)
 *
 * Day/week toggle scopes the window. Events are grouped by day and rendered
 * top-down (today first). Click a Google/O365 event → opens in their webapp;
 * a CRM event → fires `onSelectDeal` if a parent wired it (TODO Phase 11).
 */
import { useMemo, useState } from 'react';
import { RefreshCw, ExternalLink, MapPin, Clock, AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { useTenant } from '../../contexts/TenantContext';
import type { CalendarEvent, CalendarSource } from '../../lib/calendar';
import { ConnectAccountsCta } from './ConnectAccountsCta';
import { useAuth } from '../../contexts/AuthContext';

type RangeKey = 'today' | 'week' | 'month';

const RANGE_DAYS: Record<RangeKey, number> = {
  today: 1,
  week: 7,
  month: 30,
};

const SOURCE_BAR: Record<CalendarSource, string> = {
  google: 'bg-sky-400',
  office365: 'bg-violet-400',
  crm: 'bg-brand-400',
};

const SOURCE_LABEL: Record<CalendarSource, string> = {
  google: 'Google',
  office365: 'Outlook',
  crm: 'CRM',
};

export function CalendarTab() {
  const { session } = useAuth();
  const { tenant } = useTenant();
  const [range, setRange] = useState<RangeKey>('week');

  const { startIso, endIso } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + RANGE_DAYS[range]);
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  }, [range]);

  const { events, loading, error, refresh } = useCalendarEvents({ startIso, endIso });

  const grouped = useMemo(() => groupByDay(events), [events]);

  // First-run state — no events, no error → likely no provider connected yet.
  const showConnectCta = !loading && !error && events.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-white/5 px-3 py-2.5">
        <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-0.5 text-[11px]">
          {(['today', 'week', 'month'] as RangeKey[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={clsx(
                'rounded-md px-2 py-1 capitalize transition',
                r === range ? 'bg-brand-600/20 text-brand-100' : 'text-neutral-400 hover:text-neutral-200',
              )}
            >
              {r}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          className="flex items-center gap-1 rounded-md p-1.5 text-neutral-400 transition hover:bg-white/5 hover:text-neutral-100 disabled:opacity-50"
          aria-label="Refresh"
          title="Refresh calendar"
        >
          <RefreshCw className={clsx('h-3.5 w-3.5', loading && 'animate-spin')} />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <div className="m-3 flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/[0.04] p-3 text-xs text-red-200">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {showConnectCta && session?.user && tenant && (
          <ConnectAccountsCta userId={session.user.id} tenantId={tenant.id} />
        )}

        {!showConnectCta && grouped.length === 0 && !loading && (
          <div className="px-4 py-8 text-center text-xs text-neutral-500">
            No events in this window. Hit refresh once you've connected an
            account, or widen the range.
          </div>
        )}

        <div className="px-3 py-2">
          {grouped.map(({ dayKey, label, items }) => (
            <section key={dayKey} className="mb-4">
              <div className="sticky top-0 z-[1] mb-1.5 -mx-1 bg-neutral-950/95 px-1 py-1 text-[10px] font-semibold uppercase tracking-widest text-neutral-500 backdrop-blur">
                {label}
              </div>
              <ul className="space-y-1.5">
                {items.map((e) => (
                  <EventRow key={e.id} event={e} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  const start = new Date(event.start_at);
  const end = new Date(event.end_at);
  const timeStr = event.is_all_day
    ? 'All day'
    : `${fmtTime(start)} → ${fmtTime(end)}`;
  const durationMin = Math.max(1, Math.round((end.getTime() - start.getTime()) / 60_000));

  const clickable = !!event.url;
  const Wrapper: React.ElementType = clickable ? 'a' : 'div';
  const wrapperProps = clickable
    ? ({ href: event.url ?? undefined, target: '_blank', rel: 'noreferrer' } as const)
    : ({} as const);

  return (
    <li>
      <Wrapper
        {...wrapperProps}
        className={clsx(
          'group flex gap-2.5 rounded-lg border border-white/5 bg-white/[0.02] p-2.5 text-xs transition',
          clickable && 'hover:border-white/15 hover:bg-white/[0.05]',
        )}
      >
        <span className={clsx('w-0.5 shrink-0 rounded-full', SOURCE_BAR[event.source])} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="truncate font-medium text-neutral-100">{event.title}</div>
            {clickable && (
              <ExternalLink className="mt-0.5 h-3 w-3 shrink-0 text-neutral-500 opacity-0 transition group-hover:opacity-100" />
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[10.5px] text-neutral-500">
            <Clock className="h-3 w-3" />
            <span>{timeStr}</span>
            {!event.is_all_day && (
              <span className="text-neutral-600">· {formatDuration(durationMin)}</span>
            )}
            <span className="ml-auto rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-neutral-400">
              {SOURCE_LABEL[event.source]}
            </span>
          </div>
          {event.location && (
            <div className="mt-1 flex items-center gap-1.5 text-[10.5px] text-neutral-500">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
        </div>
      </Wrapper>
    </li>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

function fmtTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

interface DayGroup {
  dayKey: string;
  label: string;
  items: CalendarEvent[];
}

function groupByDay(events: CalendarEvent[]): DayGroup[] {
  const map = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    const d = new Date(e.start_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)?.push(e);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const keys = Array.from(map.keys()).sort();
  return keys.map((dayKey) => {
    const d = new Date(`${dayKey}T00:00:00`);
    let label: string;
    if (sameDay(d, today)) label = 'Today';
    else if (sameDay(d, tomorrow)) label = 'Tomorrow';
    else {
      label = d.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }
    return { dayKey, label, items: map.get(dayKey) ?? [] };
  });
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
